import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import * as offlineDB from '@/lib/offlineDB';

/**
 * Hook to manage online/offline state and background sync
 */
export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const syncInProgress = useRef(false);

  // Update online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online! Syncing changes...');
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline. Changes will be saved locally.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Trigger sync when coming online
  useEffect(() => {
    if (isOnline) {
      triggerSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  // Check pending changes count
  const checkPendingCount = useCallback(async () => {
    try {
      const pending = await offlineDB.getPendingSync();
      setPendingCount(pending.length);
    } catch (err) {
      console.error('[Sync] Failed to check pending count:', err);
    }
  }, []);

  // Initial check
  useEffect(() => {
    checkPendingCount();
  }, [checkPendingCount]);

  // Listen for service worker sync messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'SYNC_NOTES') {
        // Sync requested by service worker
        checkPendingCount();
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [checkPendingCount]);

  // Trigger background sync
  const triggerSync = useCallback(async () => {
    if (syncInProgress.current || !navigator.onLine) return;
    
    syncInProgress.current = true;
    setIsSyncing(true);

    try {
      // Request background sync if supported
      if ('serviceWorker' in navigator && 'sync' in window.SyncManager) {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-notes');
      }
      
      await checkPendingCount();
    } catch (err) {
      console.error('[Sync] Background sync failed:', err);
    } finally {
      syncInProgress.current = false;
      setIsSyncing(false);
    }
  }, [checkPendingCount]);

  return {
    isOnline,
    isSyncing,
    pendingCount,
    triggerSync,
    checkPendingCount,
  };
}

/**
 * Register service worker and handle updates
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.log('[SW] Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    console.log('[SW] Service worker registered:', registration.scope);

    // Handle updates
    registration.onupdatefound = () => {
      const installingWorker = registration.installing;
      if (!installingWorker) return;

      installingWorker.onstatechange = () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New content available
            toast.info('New version available! Refresh to update.', {
              action: {
                label: 'Refresh',
                onClick: () => window.location.reload(),
              },
              duration: 10000,
            });
          } else {
            // Content cached for offline use
            toast.success('App ready for offline use!');
          }
        }
      };
    };

    return registration;
  } catch (error) {
    console.error('[SW] Service worker registration failed:', error);
    return null;
  }
}

/**
 * Request persistent storage for PWA
 */
export async function requestPersistentStorage() {
  if (navigator.storage && navigator.storage.persist) {
    const isPersisted = await navigator.storage.persisted();
    if (!isPersisted) {
      const granted = await navigator.storage.persist();
      console.log('[Storage] Persistent storage:', granted ? 'granted' : 'denied');
      return granted;
    }
    return true;
  }
  return false;
}

/**
 * Check storage usage
 */
export async function getStorageEstimate() {
  if (navigator.storage && navigator.storage.estimate) {
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage || 0,
      quota: estimate.quota || 0,
      usagePercentage: estimate.quota ? ((estimate.usage || 0) / estimate.quota) * 100 : 0,
    };
  }
  return null;
}
